import { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { getJson } from '../pages/scorekeeping/scorekeepingApi';
import { showToast } from '../components/Toast';
import { describeTargetRefresh } from '../utils/pingolfLineup';

// Owns the data-fetching, mutation, and derived-permission logic for
// MachineDetail.js. Relocated verbatim from the page component - see
// MachineDetail.test.js for the mount-time fetch fan-out this pins down.
function useMachineDetail(machineId) {
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [setupNotes, setSetupNotes] = useState('');
  const [activeTab, setActiveTab] = useState('scores');
  // The Scores tab is the scorekeeping board for this machine, one month at a
  // time. selectedPeriod stays null until somebody picks a month, which asks the
  // server for its current month, and boardPeriod is the month the board that
  // came back is actually for, so the picker can show it.
  const [scores, setScores] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [boardPeriod, setBoardPeriod] = useState(null);
  const [loadingScores, setLoadingScores] = useState(false);
  const scoresRequestId = useRef(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [users, setUsers] = useState([]);
  const [pintips, setPintips] = useState([]);
  const [loadingPintips, setLoadingPintips] = useState(false);
  const [pintipsError, setPintipsError] = useState(null);
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [issueReporterName, setIssueReporterName] = useState('');
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [issueSubmitSuccess, setIssueSubmitSuccess] = useState(false);
  const [userUpvotes, setUserUpvotes] = useState(new Set());
  const [knownIssues, setKnownIssues] = useState([]);
  const [showKnownIssueModal, setShowKnownIssueModal] = useState(false);
  const [knownIssueForm, setKnownIssueForm] = useState({ description: '', guidanceText: '' });
  const [savingKnownIssue, setSavingKnownIssue] = useState(false);
  const [knownIssueError, setKnownIssueError] = useState('');
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [loadingMaintenanceHistory, setLoadingMaintenanceHistory] = useState(false);

  // Check if user is admin for conditional UI
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const techTier = currentUser?.techTier ?? currentUser?.tech_tier ?? 0;
  const canManageMachineIssues = isAdmin || techTier >= 2;
  const canManageKnownIssues = isAdmin || techTier >= 4;

  useEffect(() => {
    loadMachine();
    loadMonths();
    loadKnownIssues();
    if (isAdmin) {
      loadUsers();
    }

    // Auto-populate reporter name if logged in (check both camelCase and snake_case)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    if (firstName || lastName) {
      setIssueReporterName(`${firstName} ${lastName}`.trim());
    }
    // Intentionally scoped to machineId only - loadMachine/loadMonths/
    // loadKnownIssues/loadUsers are plain (non-memoized) closures redefined every
    // render, so including them here would refire this on every render instead of
    // only when navigating to a different machine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId]);

  useEffect(() => {
    if (activeTab === 'scores') {
      loadScores();
    } else if (activeTab === 'pintips') {
      loadPintips();
    } else if (activeTab === 'maintenance') {
      loadMaintenanceHistory();
    }
    // Intentionally scoped to activeTab/selectedPeriod/machineId - loadScores/
    // loadPintips/loadMaintenanceHistory are plain closures redefined every
    // render, so including them would refire this on every render instead of
    // only on tab, month or machine changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedPeriod, machineId]);

  const loadMachine = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/machines/${machineId}`);
      setMachine(response.data);
      setSetupNotes(response.data.setup_notes || '');

      // Populate user upvotes from API response
      const upvotedIssues = new Set(
        response.data.issues
          .filter(issue => issue.user_has_upvoted === true)
          .map(issue => issue.id)
      );
      console.log('Loaded upvoted issues:', Array.from(upvotedIssues));
      setUserUpvotes(upvotedIssues);
    } catch (err) {
      setError('Failed to load machine');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.get('/users');
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadKnownIssues = async () => {
    try {
      const response = await apiService.getKnownIssues(machineId);
      setKnownIssues(response.data || []);
    } catch (err) {
      console.error('Failed to load known issues:', err);
    }
  };

  // The scorekeeping board, the same one the scorekeeping leaderboard and the
  // public machine page show.
  const loadScores = async () => {
    const requestId = ++scoresRequestId.current;
    try {
      setLoadingScores(true);
      const periodQuery = selectedPeriod ? `?period=${encodeURIComponent(selectedPeriod)}` : '';
      const board = await getJson(`/machines/${machineId}/board${periodQuery}`);
      if (requestId !== scoresRequestId.current) return;
      setScores(board.entries || []);
      setBoardPeriod(board.period || null);
    } catch (err) {
      if (requestId !== scoresRequestId.current) return;
      console.error('Failed to load scores:', err);
      setScores([]);
    } finally {
      if (requestId === scoresRequestId.current) setLoadingScores(false);
    }
  };

  // The competition months for the picker. The server always includes the
  // month in progress, even before anybody has played in it.
  const loadMonths = async () => {
    try {
      const list = await getJson('/months');
      setMonths(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load months:', err);
      setMonths([]);
    }
  };

  const loadPintips = async () => {
    if (!machine?.opdb_id) {
      setPintipsError('Tips and tricks are not yet available for this machine.');
      return;
    }

    try {
      setLoadingPintips(true);
      setPintipsError(null);
      const response = await apiService.get(`/machines/${machineId}/pintips`);
      setPintips(response.data.pintips || []);
    } catch (err) {
      console.error('Failed to load pintips:', err);
      setPintipsError('Unable to load pintips at this time.');
      setPintips([]);
    } finally {
      setLoadingPintips(false);
    }
  };

  const loadMaintenanceHistory = async () => {
    try {
      setLoadingMaintenanceHistory(true);
      const response = await apiService.getMaintenanceHistory(machineId);
      setMaintenanceHistory(response.data || []);
    } catch (err) {
      console.error('Failed to load maintenance history:', err);
    } finally {
      setLoadingMaintenanceHistory(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await apiService.patch(`/machines/${machineId}/setup-notes`, {
        setupNotes: setupNotes
      });
      setEditingNotes(false);
      loadMachine();
    } catch (err) {
      setError('Failed to save notes');
      console.error(err);
    }
  };

  const handleGenerateQR = async () => {
    try {
      const response = await apiService.get(`/machines/${machineId}/qr-code`);
      const link = document.createElement('a');
      link.href = response.data.qrCode;
      link.download = `${response.data.machineName.replace(/\s+/g, '_')}_QR.png`;
      link.click();
    } catch (err) {
      setError('Failed to generate QR code');
      console.error(err);
    }
  };

  const handleOpenEditModal = () => {
    // Find the owner user to display their name in the searchable input
    const ownerUser = users.find(u => u.id === machine.owner_user_id);
    const ownerDisplay = ownerUser
      ? `${ownerUser.first_name} ${ownerUser.last_name} (${ownerUser.username})`
      : 'Club Machine (No Owner)';

    setEditForm({
      ownerUserId: machine.owner_user_id || null,
      ownerDisplay: ownerDisplay,
      // Status comes from the open tickets. All the form holds is whether the
      // machine is still in the club: 'removed' retires it, 'active' keeps it
      // (or brings it back) with its status following its tickets.
      status: machine.status === 'removed' ? 'removed' : 'active',
      pingolfTargetScore: machine.pingolf_target_score || '',
      pingolfBallCount: machine.pingolf_ball_count || '',
      wordpressPostId: machine.wordpress_post_id || '',
      hasInsiderConnected: !!machine.has_insider_connected,
      isRepin: !!machine.is_repin,
      imageUrl: machine.image_url_large || machine.image_url_medium || machine.image_url || ''
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditForm({});
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      // Re-Pin has its own endpoint, since turning it on also takes it off another machine.
      const { isRepin, ...details } = editForm;
      const response = await apiService.put(`/machines/${machineId}`, details);
      if (isRepin !== undefined && isRepin !== !!machine?.is_repin) {
        await saveRepin(isRepin);
      }
      setShowEditModal(false);
      loadMachine();
      // A changed PinGolf target is pushed to every upcoming hole that can still change, and
      // the server says how many it reached (and how many kept their own target).
      const propagation = response?.data?.pingolf_target_propagation;
      if (propagation?.error) {
        showToast(propagation.error, 'error');
      } else {
        const message = describeTargetRefresh(propagation);
        if (message) showToast(message, 'success', 6000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update machine');
      console.error(err);
    }
  };

  const handleSubmitIssue = async ({ description, gameUnplayable, affectsTournamentPlay }) => {
    try {
      setSubmittingIssue(true);
      setError('');

      await apiService.post(`/machines/${machineId}/report-issue`, {
        reporterName: issueReporterName.trim(),
        description,
        gameUnplayable,
        affectsTournamentPlay
      });

      setIssueSubmitSuccess(true);
    } catch (err) {
      setError('Failed to submit issue report. Please try again.');
      console.error(err);
    } finally {
      setSubmittingIssue(false);
    }
  };

  // Saved by Edit Machine Settings, through its own endpoint: turning it on moves it off
  // whichever machine had it, so the toast says which one that was.
  async function saveRepin(isRepin) {
    try {
      const response = await apiService.put(`/machines/${machineId}/repin`, { isRepin });
      const updated = response.data;
      setMachine((prev) => ({ ...prev, is_repin: updated.is_repin }));
      if (updated.is_repin) {
        const replaced = updated.replaced_repin_name ? ` ${updated.replaced_repin_name} is no longer the Re-Pin machine.` : '';
        showToast(`${updated.name} is now the Re-Pin machine.${replaced} The website updates in a moment.`, 'success', replaced ? 7000 : undefined);
      } else {
        showToast(`${updated.name} is no longer the Re-Pin machine.`, 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not change the Re-Pin machine.', 'error');
    }
  }

  const handleReportDone = () => {
    setIssueSubmitSuccess(false);
    setShowReportIssueModal(false);
    loadMachine();
  };

  const handleUpvote = async (issueId) => {
    try {
      const response = await apiService.post(`/machines/${machineId}/issues/${issueId}/upvote`);
      console.log('Upvote response:', response.data);

      // Update local state with server response
      setUserUpvotes(prev => {
        const newSet = new Set(prev);
        if (response.data.userUpvoted) {
          newSet.add(issueId);
          console.log('Added upvote for issue:', issueId);
        } else {
          newSet.delete(issueId);
          console.log('Removed upvote for issue:', issueId);
        }
        console.log('Updated userUpvotes:', Array.from(newSet));
        return newSet;
      });

      // Update the machine state with the new upvote count
      setMachine(prevMachine => {
        if (!prevMachine) return prevMachine;

        const updatedIssues = prevMachine.issues.map(issue => {
          if (issue.id === issueId) {
            return { ...issue, upvote_count: response.data.upvoteCount };
          }
          return issue;
        });

        return { ...prevMachine, issues: updatedIssues };
      });
    } catch (err) {
      console.error('Failed to upvote:', err);
      setError('Failed to update vote. Please try again.');
    }
  };

  const openKnownIssueModal = () => {
    setKnownIssueError('');
    setKnownIssueForm({ description: '', guidanceText: '' });
    setShowKnownIssueModal(true);
  };

  const submitKnownIssue = async (e) => {
    e.preventDefault();
    if (!knownIssueForm.description.trim()) {
      setKnownIssueError('Description is required.');
      return;
    }
    try {
      setSavingKnownIssue(true);
      setKnownIssueError('');
      await apiService.createKnownIssue({
        machineId: parseInt(machineId, 10),
        description: knownIssueForm.description.trim(),
        guidanceText: knownIssueForm.guidanceText.trim() || undefined
      });
      setShowKnownIssueModal(false);
      await loadKnownIssues();
    } catch (err) {
      setKnownIssueError(err.response?.data?.error || 'Failed to create known issue');
      console.error(err);
    } finally {
      setSavingKnownIssue(false);
    }
  };

  const handleRetireKnownIssue = async (id) => {
    try {
      await apiService.retireKnownIssue(id);
      await loadKnownIssues();
    } catch (err) {
      console.error('Failed to retire known issue:', err);
    }
  };

  return {
    machine,
    loading,
    error,
    setError,
    editingNotes,
    setEditingNotes,
    setupNotes,
    setSetupNotes,
    activeTab,
    setActiveTab,
    scores,
    months,
    selectedPeriod,
    setSelectedPeriod,
    boardPeriod,
    loadingScores,
    showEditModal,
    setShowEditModal,
    editForm,
    setEditForm,
    users,
    pintips,
    loadingPintips,
    pintipsError,
    showReportIssueModal,
    setShowReportIssueModal,
    issueReporterName,
    setIssueReporterName,
    submittingIssue,
    issueSubmitSuccess,
    userUpvotes,
    knownIssues,
    showKnownIssueModal,
    setShowKnownIssueModal,
    knownIssueForm,
    setKnownIssueForm,
    savingKnownIssue,
    knownIssueError,
    maintenanceHistory,
    loadingMaintenanceHistory,
    isAdmin,
    techTier,
    canManageMachineIssues,
    canManageKnownIssues,
    loadMachine,
    handleSaveNotes,
    handleGenerateQR,
    handleOpenEditModal,
    handleCloseEditModal,
    handleEditFormChange,
    handleSaveEdit,
    handleSubmitIssue,
    handleReportDone,
    handleUpvote,
    openKnownIssueModal,
    submitKnownIssue,
    handleRetireKnownIssue
  };
}

export default useMachineDetail;
